// src/pages/LiveMap.jsx
/**
 * LiveMap — Dispatch operations awareness view.
 *
 * Data sources (authoritative):
 *   GET /tasks/       — all tasks (active + completed)
 *   GET /api/drivers  — all drivers with operational_status / current_task_id /
 *                       last_lat / last_lng (with fallback parsing for legacy shapes)
 *
 * Pins:
 *   Red    — task pickup location (unassigned / assigned / en route to pickup)
 *   Orange — task dropoff location (picked up / en route delivery / arrived)
 *   Green  — delivered/completed task dropoff (shown when hideCompleted = false)
 *   Teal   — driver last known position (if backend has location data)
 *
 * For dropoff addresses, uses stored dropoffLat/Lng from the backend geocoding
 * system when available, falling back to the Google Maps geocoder.
 * Pickup addresses prefer stored pickupLat/pickupLng and only geocode when needed.
 *
 * Polls every 30s.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader } from "@googlemaps/js-api-loader";
import { GOOGLE_MAPS_SCRIPT_ID, GOOGLE_MAPS_LIBRARIES } from "../config/googleMaps";
import { getTasks, listDrivers } from "../api/client";
import { isDropoffPhaseStatus, isTerminalTaskStatus } from "../constants/operations";
import { formatAbsoluteTime } from "../utils/dispatchOps";

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;

// Denver metro default — used when no valid markers exist
const DENVER = { lat: 39.7392, lng: -104.9903 };
const MIN_ZOOM = 9;   // Denver metro (never zoom out past this on auto-fit)
const MAX_ZOOM = 12;  // Never zoom in tighter than this on auto-fit

/**
 * Coordinate validity gate.
 * Rejects: null/undefined, NaN, out-of-range, and the 0,0 null-island
 * (Gulf of Guinea) which appears when geocoding fails and returns a 0 fallback.
 */
function isValidCoord(lat, lng) {
  if (lat == null || lng == null) return false;
  if (typeof lat !== "number" || typeof lng !== "number") return false;
  if (isNaN(lat) || isNaN(lng)) return false;
  if (lat < -90  || lat > 90)  return false;
  if (lng < -180 || lng > 180) return false;
  // 0,0 is the Gulf of Guinea — never a valid operational location
  if (lat === 0 && lng === 0) return false;
  return true;
}

function normalizeDriverPos(driver) {
  const legacy = driver?.last_known_location;
  let legacyLat = null;
  let legacyLng = null;

  if (legacy && typeof legacy === "object") {
    legacyLat = legacy.lat ?? legacy.latitude ?? null;
    legacyLng = legacy.lng ?? legacy.longitude ?? null;
  } else if (typeof legacy === "string") {
    try {
      const parsed = JSON.parse(legacy);
      legacyLat = parsed?.lat ?? parsed?.latitude ?? null;
      legacyLng = parsed?.lng ?? parsed?.longitude ?? null;
    } catch {
      legacyLat = null;
      legacyLng = null;
    }
  }

  const rawLat = driver?.last_lat ?? driver?.lastLat ?? driver?.lat ?? legacyLat;
  const rawLng = driver?.last_lng ?? driver?.lastLng ?? driver?.lng ?? legacyLng;

  const lat = typeof rawLat === "number" ? rawLat : parseFloat(rawLat);
  const lng = typeof rawLng === "number" ? rawLng : parseFloat(rawLng);

  return { lat, lng };
}

function driverStatusLabel(driver) {
  return driver?.operational_status || driver?.current_status || "On Shift";
}

// ── Geocode cache (address → {lat, lng, formatted}) ──────────────────────────
const loadJSON = (k, d) => { try { return JSON.parse(localStorage.getItem(k) || ""); } catch { return d; } };
const saveJSON = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} };

// ── Styles ───────────────────────────────────────────────────────────────────
const styles = {
  page:    { maxWidth: 1200, margin: "28px auto", padding: "0 16px" },
  card:    { background: "#fff", borderRadius: 16, boxShadow: "0 8px 28px rgba(0,0,0,.05)", overflow: "hidden" },
  header:  { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", borderBottom: "1px solid #eee" },
  title:   { fontWeight: 800, fontSize: 20 },
  mapWrap: { height: 560 },
  statsBar:{
    display: "flex", gap: 24, padding: "10px 20px", borderBottom: "1px solid #f3f4f6",
    background: "#fafafa", flexWrap: "wrap",
  },
  statItem:{ display: "flex", flexDirection: "column" },
  statVal: { fontWeight: 800, fontSize: 18, lineHeight: 1.1 },
  statLbl: { fontSize: 11, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.06em" },
};

const pill = (bg, text = "#fff") => ({
  display: "inline-block", background: bg, color: text,
  padding: "4px 10px", borderRadius: 999, fontSize: 12, fontWeight: 700,
});

// ── Pin resolution ───────────────────────────────────────────────────────────
/**
 * Returns a pin descriptor for the given task:
 *   { address, lat, lng, color, pinLabel, infoLabel, driver }
 * lat/lng are non-null when the backend already has stored coordinates (fast path).
 */
function resolvePin(task) {
  const isDelivered = isTerminalTaskStatus(task.status);
  const isPickedUp = isDropoffPhaseStatus(task.status);

  if (isDelivered) {
    return {
      address:    task.dropoffAddress || task.deliveryAddress || "",
      lat:        task.dropoffLat  ?? null,
      lng:        task.dropoffLng  ?? null,
      color:      "#16a34a",
      pinLabel:   "Delivered",
      infoLabel:  "Delivered",
      driver:     task.driver || task.assignedDriver || "—",
    };
  }

  if (isPickedUp) {
    return {
      address:    task.dropoffAddress || task.deliveryAddress || "",
      lat:        task.dropoffLat  ?? null,
      lng:        task.dropoffLng  ?? null,
      color:      "#f97316",
      pinLabel:   "En Route — Dropoff",
      infoLabel:  "En Route to Dropoff",
      driver:     task.driver || task.assignedDriver || "Unassigned",
    };
  }

  // Unassigned / assigned / en route pickup → show pickup location
  return {
    address:    task.pickupAddress || task.pickup || "",
    lat:        task.pickupLat ?? null,
    lng:        task.pickupLng ?? null,
    color:      task.driver || task.assignedDriver ? "#3b82f6" : "#ef4444",
    pinLabel:   task.driver || task.assignedDriver ? "Assigned — Pickup" : "Unassigned — Pickup",
    infoLabel:  "Pickup Location",
    driver:     task.driver || task.assignedDriver || "UNASSIGNED",
  };
}

// ── Component ────────────────────────────────────────────────────────────────
export default function LiveMap() {
  const [hideCompleted, setHideCompleted] = useState(true);
  const [mapReady, setMapReady]           = useState(false);
  const [mapErr,   setMapErr]             = useState("");
  const [tasks,    setTasks]              = useState([]);
  const [drivers,  setDrivers]            = useState([]);
  const [lastSync, setLastSync]           = useState(null);
  const [fetchErr, setFetchErr]           = useState("");

  const mapRef      = useRef(null);
  const gmapRef     = useRef(null);
  const markersRef  = useRef([]);
  const taskMarkersRef = useRef(new Map());
  const driverMarkersRef = useRef(new Map());
  const infoRef     = useRef(null);
  const geoCache    = useMemo(() => loadJSON("ci_geocode_cache", {}), []);
  // fitBounds is called at most once (first data load). After that, the
  // dispatcher controls zoom/pan freely. Set back to false by handleRecenter.
  const fitDoneRef  = useRef(false);
  // Diagnostic: count coords filtered per render cycle (logged to console)
  const filteredRef = useRef(0);

  const consumeFocusPreference = () => {
    try {
      const taskId = window.sessionStorage.getItem("ci_live_map_focus_task_id");
      const driverId = window.sessionStorage.getItem("ci_live_map_focus_driver_id");
      return { taskId, driverId };
    } catch {
      return { taskId: null, driverId: null };
    }
  };

  const clearFocusPreference = ({ taskId, driverId }) => {
    try {
      if (taskId) window.sessionStorage.removeItem("ci_live_map_focus_task_id");
      if (driverId) window.sessionStorage.removeItem("ci_live_map_focus_driver_id");
    } catch {
      // ignore storage failures
    }
  };

  // ── Backend data fetch ─────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    try {
      const [tRes, dRes] = await Promise.all([getTasks(), listDrivers()]);
      if (tRes.ok) {
        const d = await tRes.json();
        setTasks(Array.isArray(d) ? d : []);
      }
      if (dRes.ok) {
        const d = await dRes.json();
        setDrivers(Array.isArray(d) ? d : []);
      }
      setLastSync(new Date());
      setFetchErr("");
    } catch (e) {
      setFetchErr(e.message);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, 30_000);
    const refetch = () => fetchData();
    const handleVisibility = () => {
      if (document.visibilityState === "visible") fetchData();
    };
    window.addEventListener("focus", refetch);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", refetch);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [fetchData]);

  // ── Google Maps init ───────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const loader = new Loader({
          id: GOOGLE_MAPS_SCRIPT_ID,
          apiKey: MAPS_KEY,
          version: "weekly",
          libraries: GOOGLE_MAPS_LIBRARIES,
        });
        const google = await loader.load();
        if (cancelled || !mapRef.current) return;
        gmapRef.current = new google.maps.Map(mapRef.current, {
          center: { lat: 39.7392, lng: -104.9903 },
          zoom: 9,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
        });
        infoRef.current = new google.maps.InfoWindow();
        setMapReady(true);
      } catch {
        setMapErr("Map failed to load. Check REACT_APP_GOOGLE_MAPS_KEY.");
        setMapReady(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Manual re-center (user-controlled; does NOT run automatically) ─────────
  const handleRecenter = () => {
    const google = window.google;
    if (!gmapRef.current || !google) return;
    const bounds = new google.maps.LatLngBounds();
    markersRef.current.forEach((m) => bounds.extend(m.getPosition()));
    if (!bounds.isEmpty()) {
      gmapRef.current.fitBounds(bounds, { top: 60, right: 60, bottom: 60, left: 60 });
      google.maps.event.addListenerOnce(gmapRef.current, "idle", () => {
        const z = gmapRef.current.getZoom();
        if (z > MAX_ZOOM) gmapRef.current.setZoom(MAX_ZOOM);
        else if (z < MIN_ZOOM) gmapRef.current.setZoom(MIN_ZOOM);
      });
    } else {
      gmapRef.current.setCenter(DENVER);
      gmapRef.current.setZoom(10);
    }
  };

  // ── Geocode helper (cache + Google fallback) ───────────────────────────────
  async function geocodeAddress(address) {
    if (!address) return null;
    const key = address.trim().toLowerCase();
    if (geoCache[key]) return geoCache[key];
    const google = window.google;
    if (!google || !gmapRef.current) return null;
    const geocoder = new google.maps.Geocoder();
    return new Promise((resolve) => {
      geocoder.geocode({ address }, (results, status) => {
        if (status === "OK" && results?.[0]?.geometry?.location) {
          const loc = {
            lat:       results[0].geometry.location.lat(),
            lng:       results[0].geometry.location.lng(),
            formatted: results[0].formatted_address,
          };
          geoCache[key] = loc;
          saveJSON("ci_geocode_cache", geoCache);
          resolve(loc);
        } else {
          resolve(null);
        }
      });
    });
  }

  // ── Draw markers ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapReady || !gmapRef.current) return;

    // Clear previous markers
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];
    taskMarkersRef.current = new Map();
    driverMarkersRef.current = new Map();

    const google = window.google;
    const bounds = new google.maps.LatLngBounds();
    let cancelled = false;
    filteredRef.current = 0;

    (async () => {
      // ── Task pins ────────────────────────────────────────────────────────
      const displayTasks = tasks.filter((t) => {
        const isTerminal = isTerminalTaskStatus(t.status);
        if (hideCompleted && isTerminal) return false;
        return true;
      });

      for (const task of displayTasks) {
        if (cancelled) break;
        const pin = resolvePin(task);
        if (!pin.address) continue;

        // Use stored backend coords for dropoff (fast path, no geocode cost).
        // Validate rigorously — 0,0 from a failed geocode must not render.
        let pos = null;
        if (pin.lat != null && pin.lng != null) {
          if (!isValidCoord(pin.lat, pin.lng)) {
            console.warn("[LiveMap] Filtered invalid stored coord:", {
              taskId: task.id, lat: pin.lat, lng: pin.lng, address: pin.address,
            });
            filteredRef.current += 1;
            continue;
          }
          pos = { lat: pin.lat, lng: pin.lng, formatted: pin.address };
        } else {
          pos = await geocodeAddress(pin.address);
          if (pos && !isValidCoord(pos.lat, pos.lng)) {
            console.warn("[LiveMap] Filtered invalid geocoded coord:", {
              taskId: task.id, lat: pos.lat, lng: pos.lng, address: pin.address,
            });
            filteredRef.current += 1;
            pos = null;
          }
        }
        if (!pos) continue;

        const marker = new google.maps.Marker({
          position: { lat: pos.lat, lng: pos.lng },
          map: gmapRef.current,
          title: `${task.clientName || task.client || "Task"} — ${pin.pinLabel}`,
          icon: {
            path:          google.maps.SymbolPath.CIRCLE,
            fillColor:     pin.color,
            fillOpacity:   1,
            strokeColor:   "#fff",
            strokeWeight:  2,
            scale:         9,
          },
        });

        const openTaskInfo = () => {
          const deadline  = task.deliveryDeadline
            ? new Date(task.deliveryDeadline).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
            : "—";
          const locStatus = task.locationStatus || "unknown";
          const html = `
            <div style="min-width:260px;font-family:system-ui,sans-serif">
              <div style="font-weight:800;font-size:14px;margin-bottom:2px">
                ${task.clientName || task.client || "Task"}
              </div>
              <div style="font-size:11px;color:#6b7280;margin-bottom:8px">
                ${task.caseReference || task.id?.substring(0, 8) || "—"}
              </div>
              <div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:8px">
                <span style="background:${pin.color};color:#fff;border-radius:999px;padding:2px 8px;font-size:11px;font-weight:700">
                  ${task.status || "Unscheduled"}
                </span>
                ${task.taskType ? `<span style="background:#e0e7ff;color:#3730a3;border-radius:999px;padding:2px 8px;font-size:11px;font-weight:700">${task.taskType.toUpperCase()}</span>` : ""}
              </div>
              <div style="font-size:12px;color:#374151;line-height:1.7">
                <div>📍 ${pin.infoLabel}: ${pos.formatted || pin.address}</div>
                <div>🚗 Driver: <strong>${pin.driver}</strong></div>
                <div>⏰ Deadline: ${deadline}</div>
                ${locStatus !== "geocoded" && locStatus !== "manual_override"
                  ? `<div style="color:#d97706;font-weight:600;margin-top:4px">⚠ Location: ${locStatus}</div>`
                  : ""}
              </div>
            </div>`;
          infoRef.current.setContent(html);
          infoRef.current.open(gmapRef.current, marker);
        };

        marker.addListener("click", openTaskInfo);
        marker.__openInfo = openTaskInfo;

        markersRef.current.push(marker);
        taskMarkersRef.current.set(task.id, marker);
        bounds.extend(marker.getPosition());
      }

      // ── Driver pins ──────────────────────────────────────────────────────
      for (const driver of drivers) {
        if (cancelled) break;

        const driverPos = normalizeDriverPos(driver);

        if (!isValidCoord(driverPos.lat, driverPos.lng)) {
          // Silently skip drivers with no location data — they are shown in stats only.
          // Only warn when data exists but is invalid.
          if (
            driver.last_lat != null || driver.last_lng != null ||
            driver.lastLat != null || driver.lastLng != null ||
            driver.last_known_location != null
          ) {
            console.warn("[LiveMap] Filtered invalid driver coord:", {
              driverId: driver.id, name: driver.name,
              last_lat: driver.last_lat ?? driver.lastLat,
              last_lng: driver.last_lng ?? driver.lastLng,
              last_known_location: driver.last_known_location ?? null,
            });
            filteredRef.current += 1;
          }
          continue;
        }

        const driverMarker = new google.maps.Marker({
          position: { lat: driverPos.lat, lng: driverPos.lng },
          map: gmapRef.current,
          title: `${driver.name || driver.email} — ${driverStatusLabel(driver)}`,
          icon: {
            path:         google.maps.SymbolPath.CIRCLE,
            fillColor:    "#0ea5e9",
            fillOpacity:  1,
            strokeColor:  "#fff",
            strokeWeight: 3,
            scale:        11,
          },
          label: {
            text: "D",
            color: "#ffffff",
            fontWeight: "700",
            fontSize: "11px",
          },
          zIndex: 100,
          optimized: false,
        });

        const openDriverInfo = () => {
          const locStr = `(${driverPos.lat.toFixed(4)}, ${driverPos.lng.toFixed(4)})`;
          const html = `
            <div style="min-width:200px;font-family:system-ui,sans-serif">
              <div style="font-weight:800;font-size:14px;margin-bottom:6px">
                🚗 ${driver.name || driver.email}
              </div>
              <div style="font-size:12px;color:#374151;line-height:1.8">
                <div>Status: <strong>${driverStatusLabel(driver)}</strong></div>
                <div>Task: ${driver.current_task_id || "None"}</div>
                <div>Location: ${locStr}</div>
                <div>Last seen: ${driver.updated_at ? formatAbsoluteTime(driver.updated_at) : "—"}</div>
              </div>
            </div>`;
          infoRef.current.setContent(html);
          infoRef.current.open(gmapRef.current, driverMarker);
        };

        driverMarker.addListener("click", openDriverInfo);
        driverMarker.__openInfo = openDriverInfo;

        markersRef.current.push(driverMarker);
        driverMarkersRef.current.set(driver.id, driverMarker);
        bounds.extend(driverMarker.getPosition());
      }

      if (filteredRef.current > 0) {
        console.warn(`[LiveMap] ${filteredRef.current} invalid coordinate(s) filtered this render cycle.`);
      }

      // ── Auto-fit: ONCE after first data load only ────────────────────────
      // After that, the dispatcher controls zoom/pan freely.
      // Use handleRecenter() for a user-initiated re-fit.
      if (!fitDoneRef.current) {
        if (!bounds.isEmpty() && markersRef.current.length > 0) {
          fitDoneRef.current = true;
          gmapRef.current.fitBounds(bounds, { top: 60, right: 60, bottom: 60, left: 60 });
          // After Google finishes animating, cap the zoom to a sensible range.
          google.maps.event.addListenerOnce(gmapRef.current, "idle", () => {
            const z = gmapRef.current.getZoom();
            if (z > MAX_ZOOM) gmapRef.current.setZoom(MAX_ZOOM);
            else if (z < MIN_ZOOM) gmapRef.current.setZoom(MIN_ZOOM);
          });
        } else if (!bounds.isEmpty()) {
          // Markers were cleared but bounds exist — still fit once
          fitDoneRef.current = true;
          gmapRef.current.fitBounds(bounds, { top: 60, right: 60, bottom: 60, left: 60 });
        }
        // If no valid markers at all, map stays at Denver zoom:9 (set in init)
      }

      const { taskId, driverId } = consumeFocusPreference();
      const focusedDriverMarker = driverId ? driverMarkersRef.current.get(driverId) : null;
      const focusedTaskMarker = taskId ? taskMarkersRef.current.get(taskId) : null;
      const focusedMarker = focusedDriverMarker || focusedTaskMarker;
      if (focusedMarker) {
        gmapRef.current.panTo(focusedMarker.getPosition());
        if ((gmapRef.current.getZoom() || 0) < 13) {
          gmapRef.current.setZoom(13);
        }
        if (typeof focusedMarker.__openInfo === "function") {
          focusedMarker.__openInfo();
        }
        clearFocusPreference({ taskId, driverId });
      }
    })();

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, drivers, hideCompleted, mapReady]);

  // ── Derived stats for header ───────────────────────────────────────────────
  const activeTasks  = tasks.filter((t) => !isTerminalTaskStatus(t.status));
  const unassigned   = activeTasks.filter((t) => !(t.driver || t.assignedDriver));
  const invalidLoc   = activeTasks.filter(
    (t) => t.locationStatus === "geocode_failed" || t.locationStatus === "geocode_approximate" || t.locationStatus === "no_address" ||
            (!t.locationStatus && t.dropoffLat == null && t.dropoffLng == null)
  );
  // GET /api/drivers returns field as "operational_status"
  const onShiftDrivers = drivers.filter(
    (d) => d.operational_status && !["offline", "available"].includes((d.operational_status || "").toLowerCase())
  );

  return (
    <div style={styles.page}>
      <div style={styles.card}>

        {/* Header */}
        <div style={styles.header}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={styles.title}>Live Map</span>
            {mapReady  && !mapErr  && <span style={pill("#10b981")}>Ready</span>}
            {!mapReady && !mapErr  && <span style={pill("#6b7280")}>Loading…</span>}
            {mapErr               && <span style={pill("#ef4444")}>{mapErr}</span>}
            {fetchErr             && <span style={pill("#f97316")}>Sync error</span>}
          </div>
          <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
            {lastSync && (
              <span style={{ fontSize: 12, color: "#6b7280" }}>
                Updated {lastSync.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
            <button
              onClick={handleRecenter}
              title="Fit map to all current markers"
              style={{
                fontSize: 12, fontWeight: 600, cursor: "pointer",
                padding: "4px 10px", borderRadius: 6,
                border: "1px solid #d1d5db", background: "#f9fafb", color: "#374151",
              }}
            >
              Re-center
            </button>
            <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={hideCompleted}
                onChange={(e) => setHideCompleted(e.target.checked)}
              />
              Hide completed
            </label>
          </div>
        </div>

        {/* Stats bar */}
        <div style={styles.statsBar}>
          <div style={styles.statItem}>
            <span style={styles.statVal}>{activeTasks.length}</span>
            <span style={styles.statLbl}>Active Tasks</span>
          </div>
          <div style={{ width: 1, background: "#e5e7eb", alignSelf: "stretch" }} />
          <div style={styles.statItem}>
            <span style={{ ...styles.statVal, color: unassigned.length > 0 ? "#dc2626" : "#111" }}>
              {unassigned.length}
            </span>
            <span style={styles.statLbl}>Unassigned</span>
          </div>
          <div style={{ width: 1, background: "#e5e7eb", alignSelf: "stretch" }} />
          <div style={styles.statItem}>
            <span style={{ ...styles.statVal, color: onShiftDrivers.length > 0 ? "#0ea5e9" : "#111" }}>
              {onShiftDrivers.length}
            </span>
            <span style={styles.statLbl}>Drivers On Shift</span>
          </div>
          <div style={{ width: 1, background: "#e5e7eb", alignSelf: "stretch" }} />
          <div style={styles.statItem}>
            <span style={{ ...styles.statVal, color: invalidLoc.length > 0 ? "#d97706" : "#111" }}>
              {invalidLoc.length}
            </span>
            <span style={styles.statLbl}>Location Issues</span>
          </div>
          {/* Legend */}
          <div style={{ marginLeft: "auto", display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
            {[
              { color: "#ef4444", label: "Unassigned Pickup" },
              { color: "#3b82f6", label: "Assigned Pickup"   },
              { color: "#f97316", label: "En Route Dropoff"  },
              { color: "#16a34a", label: "Delivered"         },
              { color: "#0ea5e9", label: "Driver Position"   },
            ].map(({ color, label }) => (
              <div key={label} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "#374151" }}>
                <div style={{ width: 10, height: 10, borderRadius: "50%", background: color, flexShrink: 0 }} />
                {label}
              </div>
            ))}
          </div>
        </div>

        {/* Map */}
        <div ref={mapRef} style={styles.mapWrap} />

      </div>
    </div>
  );
}
